import { type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import { trackPageView } from '../../services/pageViewService';
import { useLayoutData } from '../../contexts/LayoutDataContext';
import { FooterProvider } from '../../contexts/FooterContext';
import { ServicesProvider } from '../../contexts/ServicesContext';
import { NewsProvider } from '../../contexts/NewsContext';
interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const logoUrl = '/stackway/logo/favicon.png';
  const { footerData } = useLayoutData();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname || '/';
    if (path === '/' || path === '') {
      trackPageView('/');
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <ServicesProvider>
      <NewsProvider>
        <FooterProvider footerData={footerData}>
          <Header />
          {children}
      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-16">
            {/* About Column */}
            <div>
              <a href="/" className="inline-flex items-center gap-2 md:gap-3 mb-4 md:mb-6 hover:opacity-80 transition-opacity cursor-pointer">
                <img src={`${typeof window !== 'undefined' ? window.location.origin : ''}/stackway/logo/logo.png`} alt="StackWay Logo" className="w-10 md:w-12 h-10 md:h-12 object-contain" />
                <h3 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>StackWay</h3>
              </a>
              <p className="text-gray-300 mb-4 md:mb-6 leading-relaxed text-xs md:text-sm">
                {footerData?.footer?.about || 'Hiện thực hóa ý tưởng của bạn với công nghệ tiên tiến và dịch vụ chuyên nghiệp nhất.'}
              </p>
              <div className="flex gap-3 md:gap-4">
                {footerData?.footer?.social?.facebook ? (
                  <a
                    href={footerData.footer.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-all duration-300 cursor-pointer transform hover:scale-110"
                  >
                    <i className="ri-facebook-fill text-white text-base md:text-lg"></i>
                  </a>
                ) : (
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                    <i className="ri-facebook-fill text-white text-base md:text-lg"></i>
                  </div>
                )}
                {footerData?.footer?.social?.zalo ? (
                  <a
                    href={footerData.footer.social.zalo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-all duration-300 cursor-pointer transform hover:scale-110"
                  >
                    <span className="text-white text-lg md:text-xl font-bold">Z</span>
                  </a>
                ) : (
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                    <span className="text-white text-lg md:text-xl font-bold">Z</span>
                  </div>
                )}
                {footerData?.footer?.social?.linkedin ? (
                  <a
                    href={footerData.footer.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-all duration-300 cursor-pointer transform hover:scale-110"
                  >
                    <i className="ri-linkedin-fill text-white text-base md:text-lg"></i>
                  </a>
                ) : (
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                    <i className="ri-linkedin-fill text-white text-base md:text-lg"></i>
                  </div>
                )}
                {footerData?.footer?.social?.youtube ? (
                  <a
                    href={footerData.footer.social.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center hover:bg-[#00C897] transition-all duration-300 cursor-pointer transform hover:scale-110"
                  >
                    <i className="ri-youtube-fill text-white text-base md:text-lg"></i>
                  </a>
                ) : (
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-[#2A4B7C] rounded-full flex items-center justify-center">
                    <i className="ri-youtube-fill text-white text-base md:text-lg"></i>
                  </div>
                )}
              </div>
            </div>

            {/* Dynamic Columns from API */}
            {footerData?.footer?.columns?.slice(0, 2).map((column: any, index: number) => (
              <div key={index} className={index === 1 ? 'ml-0 md:ml-4 lg:ml-8' : ''}>
                {column.title && (
                  <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{column.title}</h4>
                )}
                {column.links && column.links.length > 0 && (
                  <ul className="space-y-2 text-gray-300">
                    {column.links.map((link: any, linkIndex: number) => (
                      <li key={linkIndex}>
                        <a
                          href={link.url}
                          className="text-xs md:text-sm hover:text-[#00C897] transition-colors cursor-pointer hover:underline block md:whitespace-nowrap"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Contact Column */}
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Liên hệ</h4>
              <div className="space-y-2.5 md:space-y-3 text-gray-300">
                {footerData?.footer?.contact?.country && (
                  <div className="flex items-start md:items-center gap-2 md:gap-3">
                    <i className="ri-map-pin-line text-[#00C897] text-sm md:text-base flex-shrink-0 mt-0.5 md:mt-0"></i>
                    <span className="text-xs md:text-sm break-words md:whitespace-nowrap">{footerData.footer.contact.country}</span>
                  </div>
                )}
                {footerData?.footer?.contact?.phone && (
                  <div className="flex items-center gap-2 md:gap-3">
                    <i className="ri-phone-line text-[#00C897] text-sm md:text-base flex-shrink-0"></i>
                    <span className="text-xs md:text-sm">{footerData.footer.contact.phone}</span>
                  </div>
                )}
                {footerData?.footer?.contact?.emails && footerData.footer.contact.emails.length > 0 && (
                  <div className="flex items-start md:items-center gap-2 md:gap-3">
                    <i className="ri-mail-line text-[#00C897] text-sm md:text-base flex-shrink-0 mt-0.5 md:mt-0"></i>
                    <span className="text-xs md:text-sm break-all md:whitespace-nowrap">{footerData.footer.contact.emails[0]}</span>
                  </div>
                )}
                {footerData?.footer?.contact?.workingHours && (
                  <div className="flex items-center gap-2 md:gap-3">
                    <i className="ri-time-line text-[#00C897] text-sm md:text-base flex-shrink-0"></i>
                    <span className="text-xs md:text-sm">{footerData.footer.contact.workingHours}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-10 md:mt-16 pt-6 md:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
              {footerData?.footer?.copyright && (
                <p className="text-gray-400 text-xs md:text-sm text-center md:text-left">
                  {footerData.footer.copyright}
                </p>
              )}
              {footerData?.footer?.poweredBy && (
                <a href="/chinh-sach-bao-mat" rel="noopener noreferrer" className="text-gray-400 text-xs md:text-sm text-center md:text-right hover:text-[#00C897] transition-colors cursor-pointer">
                  {footerData.footer.poweredBy}
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>
        </FooterProvider>
      </NewsProvider>
    </ServicesProvider>
  );
};

export default Layout;
